import type { ReactNode } from 'react';
import { site } from '@/content/site';
import { resume } from '@/content/resume';
import { projects } from '@/content/projects';

function Dot() {
  return <span aria-hidden="true" className="text-rule">·</span>;
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="py-8 border-t border-rule">
      <h2 className="label mb-6">{title}</h2>
      {children}
    </section>
  );
}

function Bullets({ items }: { items: readonly string[] }) {
  return (
    <ul className="list-disc pl-5 space-y-2 marker:text-accent max-w-prose">
      {items.map((bullet, i) => (
        <li key={i}>{bullet}</li>
      ))}
    </ul>
  );
}

// One row shape across every section: date in the left rail, title with an
// optional right-aligned location, an optional subtitle, then the body.
function Entry({
  period,
  title,
  location,
  subtitle,
  children,
}: {
  period: string;
  title: string;
  location?: string;
  subtitle?: string;
  children?: ReactNode;
}) {
  return (
    <li className="grid grid-cols-1 md:grid-cols-[10rem_1fr] gap-2 md:gap-8">
      <div className="label pt-1.5">{period}</div>
      <div>
        <div className="flex flex-wrap items-baseline justify-between gap-x-4">
          <h3 className="font-display text-xl font-medium">{title}</h3>
          {location ? <span className="text-ink-muted">{location}</span> : null}
        </div>
        {subtitle ? <div className="text-ink-muted mb-3">{subtitle}</div> : null}
        {children}
      </div>
    </li>
  );
}

export function Resume() {
  return (
    <article className="py-4 md:py-12">
      <h1
        className="font-display font-medium leading-[1.02] tracking-[-0.01em] mb-4"
        style={{ fontSize: 'var(--size-h1)' }}
      >
        {site.fullName}
      </h1>

      <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-ink-muted mb-8">
        <span>{site.location}</span>
        <Dot />
        <a href={`tel:${resume.phone.replace(/[^0-9]/g, '')}`}>{resume.phone}</a>
        <Dot />
        <a href={`mailto:${site.email}`}>{site.email}</a>
        <Dot />
        <a href="https://www.linkedin.com/in/tkasparaitis" target="_blank" rel="noreferrer">
          linkedin.com/in/tkasparaitis
        </a>
        <Dot />
        <a href="https://github.com/t-kasparaitis" target="_blank" rel="noreferrer">
          github.com/t-kasparaitis
        </a>
      </p>

      <Section title="Technical Skills">
        <dl className="space-y-3">
          <div className="grid grid-cols-1 sm:grid-cols-[8rem_1fr] gap-1 sm:gap-6">
            <dt className="label pt-1">Languages</dt>
            <dd>{resume.skills.languages.join(', ')}</dd>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-[8rem_1fr] gap-1 sm:gap-6">
            <dt className="label pt-1">Technologies</dt>
            <dd>{resume.skills.technologies.join(', ')}</dd>
          </div>
        </dl>
      </Section>

      <Section title="Experience">
        <ol className="space-y-9">
          {resume.experience.map((job, i) => (
            <Entry
              key={i}
              period={job.period}
              title={job.role}
              location={job.location}
              subtitle={job.org}
            >
              <Bullets items={job.bullets} />
            </Entry>
          ))}
        </ol>
      </Section>

      <Section title="Projects">
        <ol className="space-y-9">
          {projects.map((project, i) => (
            <Entry key={i} period={project.period} title={project.title}>
              <p className="max-w-prose mb-3">{project.description}</p>
              <ul className="flex flex-wrap gap-x-3 gap-y-1">
                {project.stack.map((tech) => (
                  <li key={tech} className="label">
                    {tech}
                  </li>
                ))}
              </ul>
            </Entry>
          ))}
        </ol>
      </Section>

      <Section title="Education">
        <ol className="space-y-9">
          {resume.education.map((school, i) => (
            <Entry
              key={i}
              period={school.period}
              title={school.school}
              location={school.location}
              subtitle={school.detail}
            />
          ))}
        </ol>
      </Section>
    </article>
  );
}
